import { ColorFieldInput } from "containers/theme-setter/components/ColorFieldInput";
import { FieldInput } from "containers/theme-setter/components/FieldInput";
import { SectionCard } from "containers/theme-setter/components/SectionCard";
import { TextVariantRow } from "containers/theme-setter/components/TextVariantRow";
import { getComponentPreview } from "containers/theme-setter/field-previews";
import type {
  ColorFieldDefinition,
  LandingFieldDefinition,
  TextVariantDisplay,
  ThemeConfig,
  ThemeContextInfo,
} from "containers/theme-setter/types";
import { fontFieldDefinitions } from "containers/theme-setter/definitions";

type ThemeSetterLeftPanelProps = {
  normalizedCustomer: string;
  showPlaceholder: boolean;
  fetchError: string | null;
  context?: ThemeContextInfo;
  themeConfig: ThemeConfig | null;
  standardColors: ColorFieldDefinition[];
  cautionColors: ColorFieldDefinition[];
  fonts: NonNullable<ThemeConfig["fonts"]>;
  components: Record<string, string>;
  landingFieldDefinitions: LandingFieldDefinition[];
  textVariants: TextVariantDisplay[];
  pendingLandingAssets: Record<string, { file: File; previewUrl: string }>;
  processingLandingFields: Record<string, boolean>;
  handleColorChange: (token: string, value: string) => void;
  handleFontChange: (field: "body" | "heading", value: string) => void;
  handleComponentChange: (key: string, value: string) => void;
  handleLandingChange: (path: string, value: string) => void;
  handleLandingAssetSelect: (
    fieldPath: string,
    file: File
  ) => Promise<void> | void;
  handleLandingAssetClear: (fieldPath: string) => void;
  handleTextVariantChange: (
    variant: string,
    field: "mobile" | "desktop",
    value: string
  ) => void;
  handleReset: () => void;
  handleSave: () => Promise<void>;
  isDirty: boolean;
  loading: boolean;
  saveError: string | null;
  saveStatus: "idle" | "saving" | "success";
  previewUrl: string;
  savingPendingAssets: boolean;
};

export const ThemeSetterLeftPanel = ({
  normalizedCustomer,
  showPlaceholder,
  fetchError,
  context,
  themeConfig,
  standardColors,
  cautionColors,
  fonts,
  components,
  landingFieldDefinitions,
  textVariants,
  pendingLandingAssets,
  processingLandingFields,
  handleColorChange,
  handleFontChange,
  handleComponentChange,
  handleLandingChange,
  handleLandingAssetSelect,
  handleLandingAssetClear,
  handleTextVariantChange,
  handleReset,
  handleSave,
  isDirty,
  loading,
  saveError,
  saveStatus,
  previewUrl,
  savingPendingAssets,
}: ThemeSetterLeftPanelProps) => {
  const isSaving = loading || saveStatus === "saving" || savingPendingAssets;
  const hasPendingAssets = Object.keys(pendingLandingAssets).length > 0;

  return (
    <div className="flex flex-col w-full lg:w-2/3 lg:h-full lg:overflow-hidden">
      <div className="space-y-2 flex-shrink-0">
        <p className="text-sm uppercase text-primary">Theme Setter</p>
        <h1 className="text-3xl font-semibold">Manage theme configuration</h1>
        <p className="text-gray-300 text-sm leading-relaxed">
          {showPlaceholder
            ? "Append ?customer=<brand-slug> to the URL to start editing a client."
            : `You're editing the "${normalizedCustomer}" theme. Missing fields will inherit the "geniusfeed" defaults.`}
        </p>
      </div>

      <div className="mt-4 flex-1 space-y-6 lg:overflow-y-auto">
        {fetchError && (
          <div className="rounded-md border border-red bg-red/10 px-4 py-3 text-sm text-red">
            Unable to load configuration: {fetchError}
          </div>
        )}

        {context && (
          <div className="rounded-md border border-primary bg-primary/10 px-4 py-3 text-sm text-primary">
            {context.source === "local" ? (
              <p>
                Loaded bundled template for{" "}
                <strong>{context.resolvedCustomer}</strong>. Saving will upload
                a new file once the API is reachable.
              </p>
            ) : context.exists ? (
              <p>
                Loaded saved configuration for{" "}
                <strong>"{context.resolvedCustomer}"</strong>.
              </p>
            ) : (
              <p>
                No saved configuration found. Using "geniusfeed" defaults for{" "}
                <strong>"{context.resolvedCustomer}"</strong>.
              </p>
            )}
          </div>
        )}

        {!showPlaceholder && themeConfig && (
          <div className="space-y-6">
            <SectionCard
              title="Colors"
              description="Update the palette tokens that power Tailwind utilities."
            >
              <div className="space-y-6">
                {standardColors.length > 0 && (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-yellow">
                        Core palette
                      </p>
                      <p className="text-sm text-gray-300">
                        Primary brand accents that are safe to customize.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {standardColors.map(({ token, value }) => (
                        <ColorFieldInput
                          key={token}
                          label={token}
                          value={value}
                          onChange={(nextValue) =>
                            handleColorChange(token, nextValue)
                          }
                          uppercaseLabel
                        />
                      ))}
                    </div>
                  </div>
                )}
                {cautionColors.length > 0 && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-yellow">
                        System palette
                      </p>
                      <p className="text-sm text-gray-300">
                        Change only when necessary and keep similar color tones
                        for consistency.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {cautionColors.map(({ token, value }) => (
                        <ColorFieldInput
                          key={token}
                          label={token}
                          value={value}
                          onChange={(nextValue) =>
                            handleColorChange(token, nextValue)
                          }
                          uppercaseLabel
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Fonts"
              description="Body and heading font stacks."
            >
              <div className="grid gap-4 md:grid-cols-2">
                {fontFieldDefinitions.map(({ label, key }) => (
                  <FieldInput
                    key={key}
                    label={label}
                    value={fonts[key] ?? ""}
                    onChange={(nextValue) => handleFontChange(key, nextValue)}
                  />
                ))}
              </div>
            </SectionCard>

            <SectionCard
              title="Components"
              description="Tailwind utility overrides for shared components."
            >
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(components).map(([key, value]) => (
                  <FieldInput
                    key={key}
                    label={key}
                    value={value ?? ""}
                    onChange={(nextValue) =>
                      handleComponentChange(key, nextValue)
                    }
                    uppercaseLabel
                    preview={getComponentPreview(key)}
                  />
                ))}
              </div>
            </SectionCard>

            <SectionCard
              title="Landing"
              description="Assets and metadata used on the landing page & iframe preview."
            >
              <div className="grid gap-4 md:grid-cols-2">
                {landingFieldDefinitions.map(
                  ({
                    label,
                    path,
                    value,
                    colSpan,
                    supportsUpload,
                    preview,
                  }) => (
                    <FieldInput
                      key={path}
                      label={label}
                      value={value}
                      onChange={(nextValue) => {
                        if (pendingLandingAssets[path]) {
                          handleLandingAssetClear(path);
                        }
                        handleLandingChange(path, nextValue);
                      }}
                      onUpload={
                        supportsUpload
                          ? (file) => handleLandingAssetSelect(path, file)
                          : undefined
                      }
                      onClearUpload={
                        pendingLandingAssets[path]
                          ? () => handleLandingAssetClear(path)
                          : undefined
                      }
                      pendingUploadPreviewUrl={
                        pendingLandingAssets[path]?.previewUrl
                      }
                      pendingUploadFileName={
                        pendingLandingAssets[path]?.file.name
                      }
                      uploading={Boolean(processingLandingFields[path])}
                      className={colSpan === 2 ? "md:col-span-2" : ""}
                      preview={preview}
                    />
                  )
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Text variants"
              description="Responsive typography tokens for shared components."
            >
              <div className="grid gap-4">
                {textVariants.map(({ key, mobile, desktop, preview }) => (
                  <TextVariantRow
                    key={key}
                    variantKey={key}
                    mobile={mobile}
                    desktop={desktop}
                    onChange={(field, nextValue) =>
                      handleTextVariantChange(key, field, nextValue)
                    }
                    preview={preview}
                  />
                ))}
              </div>
            </SectionCard>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 space-y-3 pt-4">
        {!showPlaceholder && (
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <button
              className="rounded-md border border-border px-6 py-2 text-sm text-gray-200 bg-gray-800 hover:bg-gray-800/80 disabled:opacity-50"
              onClick={handleReset}
              disabled={
                (!isDirty && !hasPendingAssets) ||
                loading ||
                !themeConfig ||
                savingPendingAssets
              }
            >
              Reset Changes
            </button>

            {previewUrl && (
              <a
                className="items-center justify-center text-center rounded-md border border-border px-6 py-2 text-sm text-gray-200 bg-gray-800 hover:bg-gray-800/80 disabled:opacity-50"
                href={previewUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                Full Page Preview
              </a>
            )}
            <button
              className="rounded-md bg-yellow hover:bg-yellow/80 px-6 py-2 text-black font-semibold disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving || !themeConfig}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        )}
        {saveError && (
          <div className="rounded-md border border-red bg-red/10 px-4 py-3 text-sm text-red">
            Unable to save: {saveError}
          </div>
        )}
        {saveStatus === "success" && (
          <div className="rounded-md border border-green bg-green/10 px-4 py-3 text-sm text-green">
            Configuration saved successfully.
          </div>
        )}
      </div>
    </div>
  );
};
